import { Link } from 'react-router-dom';
import MangaCard from '@/components/feature/MangaCard';
import { getHotMangas, getLatestMangas, getCompletedMangas, type Manga } from '@/mocks/manga';

export function SectionHeader({
  badge,
  title,
  desc,
  to,
  linkLabel,
}: {
  badge: string;
  title: string;
  desc: string;
  to: string;
  linkLabel: string;
}) {
  return (
    <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
      <div>
        <span className="text-sm font-semibold uppercase tracking-widest text-primary-600">{badge}</span>
        <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">{title}</h2>
        <p className="mt-2 max-w-xl text-sm text-foreground-600">{desc}</p>
      </div>
      <Link
        to={to}
        className="whitespace-nowrap rounded-full border border-primary-300 px-5 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
      >
        {linkLabel}
        <i className="ri-arrow-right-line ml-1"></i>
      </Link>
    </div>
  );
}

export function HotSection() {
  const hot = getHotMangas(6);
  const rankStyle = ['bg-primary-500', 'bg-primary-400', 'bg-accent-500', 'bg-secondary-400', 'bg-secondary-500', 'bg-secondary-400'];
  return (
    <section className="w-full bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeader
          badge="Trending Now"
          title="本周追更热榜"
          desc="综合阅读量与追更热度排出的本周人气榜单，热门佳作一网打尽。"
          to="/category/rexue"
          linkLabel="看看热血漫画"
        />
        <ol className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
          {hot.map((m, i) => (
            <li key={m.id}>
              <Link
                to={`/manga/${m.id}`}
                className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-300"
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-base font-black text-background-50 ${rankStyle[i]}`}
                >
                  {i + 1}
                </span>
                <img
                  src={m.cover}
                  alt={`${m.title}封面缩略图`}
                  className="h-20 w-14 shrink-0 rounded-md object-cover object-top"
                />
                <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-1 text-sm font-bold text-foreground-900 group-hover:text-primary-700">
                    {m.title}
                  </h3>
                  <p className="mt-1 line-clamp-1 text-xs text-foreground-600">
                    {m.genres.join(' · ')}
                  </p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-foreground-600">
                    <span className="flex items-center gap-1 text-primary-600">
                      <i className="ri-fire-line"></i>
                      {m.views}热度
                    </span>
                    <span>{m.updateTime}更新</span>
                  </div>
                </div>
                <span className="shrink-0 rounded-md bg-accent-100 px-2.5 py-1 text-xs font-semibold text-accent-800">
                  {m.score.toFixed(1)} 分
                </span>
              </Link>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function LatestSection() {
  const latest = getLatestMangas(8);
  return (
    <section id="latest" className="w-full bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeader
          badge="Fresh Updates"
          title="最新更新"
          desc="追更不迷路，最新章节实时更新，第一时间看到心仪作品的新进度。"
          to="/category/shaonian"
          linkLabel="更多少年漫"
        />
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {latest.map((m) => (
            <MangaCard key={m.id} manga={m} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function CompletedSection() {
  const completed: Manga[] = getCompletedMangas(6);
  return (
    <section className="w-full bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeader
          badge="Completed Works"
          title="完结作品一次看个够"
          desc="全部章节完结，适合一口气追完，再也不用苦等更新。"
          to="/category/wanjie"
          linkLabel="查看完结漫画"
        />
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {completed.map((m) => (
            <Link
              key={m.id}
              to={`/manga/${m.id}`}
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300"
            >
              <div className="relative aspect-[5/7] w-full overflow-hidden">
                <img
                  src={m.cover}
                  alt={`${m.title}完结漫画封面`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-2 top-2 rounded-md bg-accent-600 px-2 py-0.5 text-xs font-medium text-background-50">
                  完结
                </span>
              </div>
              <div className="p-3">
                <h3 className="line-clamp-1 text-sm font-semibold text-foreground-900 group-hover:text-primary-700">
                  {m.title}
                </h3>
                <p className="mt-1 text-xs text-foreground-600">{m.author}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}